import { SavedQuickListItem } from "../SavedQuickListsPanel";
import {
  getRecipesAsyncUsingRecipeId,
  getTitleDescriptionTemplateByIdAsync,
  getCategoryTemplateUsingTemplateIdAsync,
  getSelectedCategoryTreeAsync,
  getMoreMappingByTemplateIdAsync,
} from "src/redux/actions/templateAndPricingActions";
import { getPlatformFieldsAsync } from "src/redux/actions/productActions";
import { getPriceTemplateByIdAsync } from "src/redux/actions/productPricingAction";
import { generateDefaultPriceRules, PriceRuleFormValue } from "../hooks/usePriceModifierFields";
import { resolvePriceRulesAfterTemplateHydrate } from "./listingProfileTemplatePriceUi";
import {
  keepMoreMappingRefs,
  type MoreMappingIdRef,
} from "./quickListMoreMappingIdentity";
import {
  fetchMiscTemplatesWithRetry,
  type MiscHydrateStore,
} from "./miscMappingHydrate";
import {
  isUsablePriceHydratePayload,
  mapPriceTemplateToQuickListForm,
  mergePriceHydratePayloads,
  needsPriceTemplateDetailFetch,
  resolvePriceTemplateId,
} from "./quickListPriceHydrate";
import {
  normalizeCategoryPlatformFieldsPayload,
  resolveCategoryPlatformFieldId,
  resolvePlatformFieldIdFromCategoryPayload,
  resolvePlatformFieldIdFromCategoryTreePath,
} from "./listingProfileCategoryFieldUtils";

/** Resolves platform field id from list/detail mapping rows (id or nested object). */
export const platformFieldIdFromMapping = (
  field: unknown,
  fallback = ""
): string => {
  if (field == null || field === "") return fallback;
  if (typeof field === "object" && field !== null && "id" in field) {
    const id = (field as { id?: number | string }).id;
    return id != null && id !== "" ? String(id) : fallback;
  }
  return String(field);
};

export interface HydrateFormContext {
  platformId: number | undefined;
  dispatch: any;
  checkFormField: string | undefined;
  priceTemplateFields: any[];
  // Conditions — written into state so Formik can receive them via initialValues on remount
  setHydratedProductRecipes: (rows: any[]) => void;
  setHydratedDataConjunction: (val: string) => void;
  setEditMappingDataDetails: (rows: any[]) => void;
  setEditingRecipeId: (id: number | null) => void;
  setRuleName: (name: string) => void;
  setStatus?: (status: "ACTIVE" | "DRAFT") => void;
  setEditSubTemplateIds: (ids: any) => void; // useState setter — also accepts (prev) => next
  setHydratingTitle: (state: boolean) => void;
  setTitleInput: (val: string) => void;
  setEditorData: (val: string) => void;
  setTagsInput: (val: string) => void;
  setSkuInput: (val: string) => void;
  setTitleAdvanceSetting: (settings: any) => void;
  setDescriptionAdvanceSetting: (settings: any) => void;
  setOpenTitleAdvanceConfig: (state: boolean) => void;
  setOpenDescriptionAdvanceConfig: (state: boolean) => void;
  setHydratingPrice: (state: boolean) => void;
  setPriceRules: (rules: PriceRuleFormValue[]) => void;
  setRepricing: (repricing: any) => void;
  setPriceAdjustmentType: (type: string) => void;
  setHydratingCategory: (state: boolean) => void;
  setCategoryUpdateData: (data: any) => void;
  setInitialCategoryState: (state: any) => void;
  setEngineState: (state: any) => void;
  setEngineKey: (key: any) => void;
  setSelectedAttributes: (attrs: any[]) => void;
  onMiscTemplatesLoaded: (loaded: MiscHydrateStore) => void;
  onPriceTemplateLoaded?: (payload: any) => void;
}

export const hydrateQuickListFormForEdit = async (
  savedItem: SavedQuickListItem,
  context: HydrateFormContext
) => {
  const {
    platformId,
    dispatch,
    checkFormField,
    priceTemplateFields,
    setHydratedProductRecipes,
    setHydratedDataConjunction,
    setEditMappingDataDetails,
    setEditingRecipeId,
    setRuleName,
    setStatus,
    setEditSubTemplateIds,
    setHydratingTitle,
    setTitleInput,
    setEditorData,
    setTagsInput,
    setSkuInput,
    setTitleAdvanceSetting,
    setDescriptionAdvanceSetting,
    setOpenTitleAdvanceConfig,
    setOpenDescriptionAdvanceConfig,
    setHydratingPrice,
    setPriceRules,
    setRepricing,
    setPriceAdjustmentType,
    setHydratingCategory,
    setCategoryUpdateData,
    setInitialCategoryState,
    setEngineState,
    setEngineKey,
    setSelectedAttributes,
    onMiscTemplatesLoaded,
    onPriceTemplateLoaded,
  } = context;

  // 1. Conditions — MUST be set in the same synchronous batch as setEditingRecipeId.
  // The Formik key depends on editingRecipeId, so when it changes Formik remounts.
  // If we set conditions AFTER an await, the await breaks React batching and Formik
  // remounts with stale default values before setHydratedProductRecipes fires.
  // These only need savedItem.mapping_data (already available, no API call needed).
  const mappedRecipes = (savedItem.mapping_data && savedItem.mapping_data.length > 0)
    ? savedItem.mapping_data.map((d: any) => ({
        id: d.id ?? null,
        checkPlatformField: platformFieldIdFromMapping(d.check_platform_field, checkFormField || ""),
        checkOperator: d.check_operator ?? "CONTAINS",
        checkConjunction: d.check_conjunction ?? "OR",
        checkValue: d.check_value ?? "",
      }))
    : [{ id: null, checkConjunction: 'OR', checkOperator: 'CONTAINS', checkPlatformField: checkFormField || '', checkValue: '' }];

  setHydratedDataConjunction(savedItem.check_data_conjunction ?? "OR");
  setHydratedProductRecipes(mappedRecipes);
  setEditMappingDataDetails(savedItem.mapping_data ?? []);
  setEditingRecipeId(savedItem.id);
  setRuleName(savedItem.name ?? "");
  if (setStatus) {
    setStatus(savedItem.status ?? "ACTIVE");
  }
  // All four setState calls above are in the same synchronous block — React 18
  // batches them into one render, so Formik remounts with the correct initialValues.

  // Fetch full recipe details to get complete sub-template relationships (e.g. other_templates nested structure)
  let fullRecipeDetail: any = null;
  try {
    const recipeRes: any = await dispatch(
      getRecipesAsyncUsingRecipeId({ recipeId: savedItem.id + "", otherParams: {} })
    );
    if (recipeRes.payload) {
      fullRecipeDetail = recipeRes.payload;
    }
  } catch (err) {
    console.error("Failed to fetch full recipe detail:", err);
  }

  if (fullRecipeDetail?.mapping_data?.length) {
    setEditMappingDataDetails(fullRecipeDetail.mapping_data);
  }

  const otherTemplates = fullRecipeDetail?.other_templates ?? savedItem.other_templates ?? [];
  const moreMappingRefs = keepMoreMappingRefs(otherTemplates);
  const recipePrice = fullRecipeDetail?.price_template ?? savedItem.price_template ?? null;
  const priceId = resolvePriceTemplateId(recipePrice);

  // Store sub-template IDs — keep templateId even when fieldId is missing.
  setEditSubTemplateIds({
    titleId: savedItem.title_description_template ?? null,
    priceId,
    categoryId: savedItem.category_template ?? null,
    moreMappingIds: moreMappingRefs,
  });

  // 2. Title/Description
  if (savedItem.title_description_template) {
    setHydratingTitle(true);
    dispatch(
      getTitleDescriptionTemplateByIdAsync({
        templateId: savedItem.title_description_template,
      })
    )
      .then((res: any) => {
        const titleData = res.payload;
        if (titleData) {
          setTitleInput(titleData.title_value_str ?? titleData.title_value_string ?? titleData.title ?? "");
          setEditorData(
            titleData.description_value_str
              ?? titleData.description_value_string
              ?? titleData.description
              ?? ""
          );
          setTagsInput(titleData.tags_value_str ?? titleData.tags_value_string ?? titleData.tags ?? "");
          setSkuInput(titleData.sku_value_str ?? titleData.sku_value_string ?? titleData.sku ?? "");
          if (titleData.title_processing_config) {
            const hasTitleAi = !!titleData.title_processing_config.isRemoveHtml || 
                               !!titleData.title_processing_config.isTranslate || 
                               !!titleData.title_processing_config.isRephrasing;
            setOpenTitleAdvanceConfig(hasTitleAi);
            setTitleAdvanceSetting({
              isRemoveHtml: !!titleData.title_processing_config.isRemoveHtml,
              isTranslate: !!titleData.title_processing_config.isTranslate,
              translate_language: titleData.title_processing_config.translate_language ?? "",
              isRephrasing: !!titleData.title_processing_config.isRephrasing,
              rephrasing_limit: titleData.title_processing_config.rephrasing_limit ?? "",
            });
          }
          if (titleData.description_processing_config) {
            const hasDescAi = !!titleData.description_processing_config.isRemoveHtml || 
                              !!titleData.description_processing_config.isTranslate || 
                              !!titleData.description_processing_config.isRephrasing;
            setOpenDescriptionAdvanceConfig(hasDescAi);
            setDescriptionAdvanceSetting({
              isRemoveHtml: !!titleData.description_processing_config.isRemoveHtml,
              isTranslate: !!titleData.description_processing_config.isTranslate,
              translate_language: titleData.description_processing_config.translate_language ?? "",
              isRephrasing: !!titleData.description_processing_config.isRephrasing,
              rephrasing_limit: titleData.description_processing_config.rephrasing_limit ?? "",
            });
          }
        }
      })
      .catch((err) => {
        console.error("Failed to hydrate title/description:", err);
      })
      .finally(() => setHydratingTitle(false));
  }

  // 3. Price — prefer template-mapping.price_template; detail GET only for multi-row rules.
  const applyPriceHydrate = (payload: any) => {
    if (!isUsablePriceHydratePayload(payload)) return;
    onPriceTemplateLoaded?.(payload);
    const mapped = mapPriceTemplateToQuickListForm(payload, priceTemplateFields);
    setRepricing({
      ...mapped.repricing,
      ...(mapped.templateIsDefault !== undefined && mapped.templateIsDefaultPayloadKey
        ? {
            isDefault: mapped.templateIsDefault,
            isDefaultPayloadKey: mapped.templateIsDefaultPayloadKey,
          }
        : {}),
    });
    setPriceAdjustmentType(mapped.adjustmentType);
    setPriceRules(
      resolvePriceRulesAfterTemplateHydrate(
        mapped.rules,
        priceTemplateFields.length,
        () => generateDefaultPriceRules(priceTemplateFields)
      )
    );
  };

  applyPriceHydrate(recipePrice);

  if (needsPriceTemplateDetailFetch(recipePrice, priceId) && platformId && priceId) {
    setHydratingPrice(true);
    dispatch(
      getPriceTemplateByIdAsync({
        templateId: priceId,
        platformId,
      })
    )
      .then((res: any) => {
        const detail = Array.isArray(res.payload)
          ? res.payload
          : res.payload?.results ?? res.payload;
        applyPriceHydrate(mergePriceHydratePayloads(recipePrice, detail));
      })
      .catch((err) => {
        console.error("Failed to hydrate price rules:", err);
      })
      .finally(() => setHydratingPrice(false));
  }

  // 4. Category
  if (savedItem.category_template) {
    setHydratingCategory(true);
    dispatch(
      getCategoryTemplateUsingTemplateIdAsync({
        category_temp_id: savedItem.category_template,
      })
    )
      .then(async (res: any) => {
        const catData = res.payload;
        setCategoryUpdateData(catData);
        if (catData?.category_data) {
          const cData = catData.category_data;
          const cId = cData?.id ?? cData;

          let fullPath = cData?.api_name ?? cData?.display_name ?? cData?.name ?? "";
          let platformFieldId = resolvePlatformFieldIdFromCategoryPayload(cData, catData);
          try {
            const treeRes = await dispatch(getSelectedCategoryTreeAsync({ category_data_id: cId }));
            const pathNodes = (treeRes as any).payload;
            if (pathNodes?.length) {
              fullPath = pathNodes.map((it: any) => it.api_name).join(' > ');
              if (platformFieldId == null) {
                platformFieldId = resolvePlatformFieldIdFromCategoryTreePath(pathNodes);
              }
            }
          } catch (err) {
            console.error("Failed to fetch full category path:", err);
          }

          if (platformFieldId == null && platformId) {
            try {
              const fieldsRes: any = await dispatch(
                getPlatformFieldsAsync({
                  platformId,
                  otherParams: { field_code: 'CATEGORY' },
                })
              );
              platformFieldId = resolveCategoryPlatformFieldId(
                normalizeCategoryPlatformFieldsPayload(fieldsRes?.payload),
                null
              );
            } catch (err) {
              console.error("Failed to resolve category platform field:", err);
            }
          }

          setInitialCategoryState({
            categoryId: cId,
            categoryPath: fullPath,
            platformFieldId,
          });
          setEngineState({
            categoryId: cId,
            categoryPath: fullPath,
            isLeafCategory: true,
            showAttributes: false,
            isAttributeLoading: false,
          });
          setEngineKey((prev: any) => prev + 1);
          if (catData.attributes && Array.isArray(catData.attributes)) {
            setSelectedAttributes(catData.attributes);
          }
        }
      })
      .catch((err) => {
        console.error("Failed to hydrate category mapping:", err);
      })
      .finally(() => setHydratingCategory(false));
  }

  // 5. More Mappings — fetch by saved template id and hand the rows back.
  // The form attaches them when the channel field catalog is available, so a
  // catalog response that lands before or after these calls still hydrates.
  // Template id is enough; platform id is often unset on the unified list.
  {
    let loadedTemplates: MiscHydrateStore["templates"] = [];
    let failedRefs: MiscHydrateStore["failedRefs"] = [];
    if (moreMappingRefs.length > 0) {
      try {
        const loaded = await fetchMiscTemplatesWithRetry(
          moreMappingRefs,
          async (templateId) => {
            const res: any = await dispatch(
              getMoreMappingByTemplateIdAsync({ templateId })
            );
            return res?.payload;
          }
        );
        loadedTemplates = loaded.templates;
        failedRefs = loaded.failedRefs;

        const resolvedRefs: MoreMappingIdRef[] = loadedTemplates
          .filter((item) => item.templateId)
          .map((item) => {
            const source = moreMappingRefs.find(
              (ref) =>
                ref.templateId === item.templateId &&
                (ref.fieldId == null ||
                  item.fieldId == null ||
                  ref.fieldId === item.fieldId)
            )
            return {
              fieldId: item.fieldId,
              templateId: item.templateId,
              linkId: source?.linkId ?? null,
            }
          });

        setEditSubTemplateIds((prev: any) => ({
          ...prev,
          moreMappingIds: resolvedRefs.length ? resolvedRefs : moreMappingRefs,
        }));
      } catch (err) {
        console.error("Failed to hydrate more mappings:", err);
        failedRefs = moreMappingRefs;
      }
    }

    onMiscTemplatesLoaded({
      recipeId: Number(savedItem.id),
      platformId: platformId ?? null,
      templates: loadedTemplates,
      failedRefs,
    });
  }

  window.scrollTo({ top: 0, behavior: "smooth" });
};
